import {get } from 'lodash';

/**
 * Find an active deal interest between the current user and the profile user
 * 
 * @param {Object} profileUser - The user profile being viewed
 * @param {Object} currentUser - The logged in user 
 * @returns {Object|null} The deal interest if found, null otherwise
 */
export const findActiveDealInterest = (profileUser, currentUser) => {
    // Basic validation 
    if (!profileUser || !currentUser) {
        console.log('FindActiveDealInterest: Missing profileUser or currentUser', { profileUser, currentUser });
        return null;
    }

    // Safely get deal interests with null/undefined checks
    const dealInterests = get(profileUser, 'inventory_user.deal_interests', []);

    // Validate dealInterests is an array
    if (!Array.isArray(dealInterests)) {
        console.log('FindActiveDealInterest: dealInterests is not an array', dealInterests);
        return null;
    }

    if (dealInterests.length === 0) {
        return null;
    }

    // Find a deal interest that's not in declined or archived state
    // and has a valid deal object with required properties
    const activeDealInterest = dealInterests.find(interest => {
        if (!interest) return false;

        const state = get(interest, 'workflow_state', '');
        const deal = get(interest, 'deal');

        // Check if deal is a valid object with at least some basic properties
        const isValidDeal = deal &&
            typeof deal === 'object' &&
            (deal.headline || deal.deal_name || deal.id);

        return state !== 'declined' && state !== 'archived' && isValidDeal;
    });

    // Extra validation to ensure the returned deal interest has a proper deal object
    if (activeDealInterest && activeDealInterest.deal) {
        // If headline is missing but deal_name exists, use deal_name as headline
        if (!activeDealInterest.deal.headline && activeDealInterest.deal.deal_name) {
            activeDealInterest.deal.headline = activeDealInterest.deal.deal_name;
        }

        return activeDealInterest;
    }

    return null;
};

/**
 * Get a badge for the deal header based on the workflow state
 * 
 * @param {Object} dealInterest - The deal interest
 * @returns {Object} Badge data with text and color properties
 */
export const getDealBadge = (dealInterest, locale = 'en') => {
    if (!dealInterest) return null;

    const workflowState = get(dealInterest, 'workflow_state');
    const isFr = locale === 'fr';

    const badgeMap = {
        'matching':                    { text: isFr ? 'Correspondance'                              : 'Matching',                  color: 'green' },
        'invited':                     { text: isFr ? 'Conseiller(ère) invité(e)'                   : 'Invited',                   color: 'blue' },
        'reviewing_proposal':          { text: isFr ? 'Examen de la proposition'                    : 'Reviewing Proposal',         color: 'orange' },
        'shortlisted':                 { text: isFr ? 'Présélectionné'                              : 'Shortlisted',               color: 'purple' },
        'out_for_receiving_signature': { text: isFr ? 'Accord de confidentialité en attente'        : 'NDA Out for Signature',      color: 'deeporange' },
        'awaiting_disclosing_signature':{ text: isFr ? 'En attente de votre signature'              : 'Awaiting Your Signature',    color: 'red' },
        'nda_completed':               { text: isFr ? 'Accord de confidentialité complété'          : 'NDA Completed',             color: 'teal' },
        'negotiating':                 { text: isFr ? 'En négociation'                              : 'Negotiating',               color: 'indigo' },
        'declined':                    { text: isFr ? 'Refusé'                                      : 'Declined',                  color: 'grey' },
        'archived':                    { text: isFr ? 'Archivé'                                     : 'Archived',                  color: 'brown' },
    };

    return badgeMap[workflowState] || { text: isFr ? 'Statut inconnu' : 'Unknown Status', color: 'grey' };
};

/**
 * Get a color for the badge
 * 
 * @param {string} color - The badge color name
 * @returns {string} The CSS color value
 */
export const getBadgeColor = (color) => {
    const colorMap = {
        'green': '#4caf50',
        'blue': '#2196f3',
        'orange': '#ff9800',
        'purple': '#9c27b0',
        'deeporange': '#ff5722',
        'red': '#f44336',
        'teal': '#009688',
        'indigo': '#3f51b5',
        'grey': '#9e9e9e',
        'brown': '#795548'
    };

    return colorMap[color] || '#9e9e9e';
};